import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    GraduationCap,
    Rocket,
    CalendarPlus,
    LayoutList,
    Presentation,
    Share2,
    BarChart3,
    ChevronDown,
    ChevronRight,
    CheckCircle2,
    ListOrdered,
    Coins,
    Search,
    ArrowRight,
} from 'lucide-react';
import styles from './TutorialsPage.module.css';

const TUTORIAL_SECTIONS = [
    {
        id: 'getting-started',
        title: 'Getting started',
        icon: Rocket,
        steps: [
            { text: 'Sign up or log in to your LivePulse account.' },
            { text: 'From the Dashboard, click **New Event** to create your first event.' },
            { text: 'Give your event a name and set the start and end dates.' },
            { text: 'After creating, open the event to add activities and share the join link.' },
        ],
    },
    {
        id: 'create-event',
        title: 'Creating an event',
        icon: CalendarPlus,
        steps: [
            { text: 'Go to **Dashboard** → **New Event**.' },
            { text: 'Enter event name, start date, and end date. Click Create.' },
            { text: 'You’ll see your event in the list. Click it to open the event page.' },
            { text: 'Use **Add activity** to add polls, Q&A, word clouds, and more.' },
        ],
        cta: { label: 'Go to Dashboard', to: '/dashboard' },
    },
    {
        id: 'activity-types',
        title: 'Activity types',
        icon: LayoutList,
        steps: [
            { text: '**Multiple choice** — Single or multiple options; great for quick polls.' },
            { text: '**Word cloud** — Collect short answers; words appear larger when repeated.' },
            { text: '**Rating** — Let the audience rate (e.g. 1–5 stars or 1–10).' },
            { text: '**Audience Q&A** — Participants submit questions; you can approve and reply live.' },
            { text: '**Quiz** — Run scored quizzes with correct answers and optional timers.' },
        ],
        cta: { label: 'Open an event to add activities', to: '/dashboard' },
    },
    {
        id: 'presenting',
        title: 'Presenting live',
        icon: Presentation,
        steps: [
            { text: 'Open your event and click **Present** (or the present icon).' },
            { text: 'The presenter view shows the current activity and live results.' },
            { text: 'Switch activities from the sidebar to show different polls or Q&A.' },
            { text: 'Responses appear in real time. You can highlight, approve, or reply to Q&A.' },
        ],
        cta: { label: 'Dashboard', to: '/dashboard' },
    },
    {
        id: 'sharing',
        title: 'Sharing with participants',
        icon: Share2,
        steps: [
            { text: 'On the event page, find the **Join link** or **Event code**.' },
            { text: 'Share the link (e.g. yoursite.com/join/CODE) with your audience.' },
            { text: 'Participants open the link, enter their name (optional), and can respond to active activities.' },
            { text: 'You can also use **QR code** if available for in-room scanning.' },
        ],
    },
    {
        id: 'analytics',
        title: 'Analytics',
        icon: BarChart3,
        steps: [
            { text: 'Go to **Analytics** in the sidebar to see aggregated stats.' },
            { text: 'View total responses, questions run, and average responses per activity.' },
            { text: 'See weekly response volume and activity type breakdown.' },
            { text: 'Use the activity performance table to see which questions got the most engagement.' },
        ],
        cta: { label: 'Open Analytics', to: '/analytics' },
    },
    {
        id: 'coins-billing',
        title: 'Coins & billing (transparency)',
        icon: Coins,
        steps: [
            { text: '**Creating a poll** — Each poll costs coins based on your event’s length. Duration is split into 4‑day blocks (e.g. 7 days = 2 blocks). Cost = (number of blocks) × (coin cost per poll). You’ll see the charge before the poll is created.' },
            { text: '**Reset results** — Clearing responses to reuse a poll counts as creating it again: the same coin amount is charged (unless you have an active subscription). Export your data first if you need it.' },
            { text: '**Extending the event end date** — Extending costs coins for the extra time: (new 4‑day blocks added) × (coin cost per poll) × (number of polls in the event). If you don’t have enough coins, the end date will not be extended and you’ll see an “Insufficient coins” message.' },
            { text: '**Shortening the event end date** — If you shorten the event, you get a **refund** only for polls that have **no responses**. Polls that already have votes keep the coins already charged.' },
            { text: '**Start date** — Once any poll in the event has at least one vote or response, the event **start date cannot be changed**. You can still change the name and end date.' },
            { text: '**Active subscription** — With an active subscription, you are not charged coins for creating polls, resetting results, or extending the event. Check **Settings** for your balance and transaction history.' },
        ],
        cta: { label: 'Settings (balance & history)', to: '/settings' },
    },
];

const StepItem = ({ step }) => {
    const parts = step.text.split(/(\*\*[^*]+\*\*)/g);
    return (
        <li className={styles.stepItem}>
            <CheckCircle2 size={16} className={styles.stepIcon} aria-hidden />
            <span>
                {parts.map((p, i) =>
                    p.startsWith('**') && p.endsWith('**') ? (
                        <strong key={i}>{p.slice(2, -2)}</strong>
                    ) : (
                        p
                    )
                )}
            </span>
        </li>
    );
};

const TutorialCard = ({ section, isOpen, onToggle }) => {
    const Icon = section.icon;
    return (
        <article className={styles.card}>
            <button
                type="button"
                className={styles.cardHeader}
                onClick={() => onToggle(section.id)}
                aria-expanded={isOpen}
                aria-controls={`tutorial-panel-${section.id}`}
                id={`tutorial-trigger-${section.id}`}
            >
                <span className={styles.cardIconWrap} aria-hidden>
                    <Icon size={20} />
                </span>
                <h2 className={styles.cardTitle}>{section.title}</h2>
                {isOpen ? (
                    <ChevronDown size={20} className={styles.chevron} aria-hidden />
                ) : (
                    <ChevronRight size={20} className={styles.chevron} aria-hidden />
                )}
            </button>
            {isOpen && (
                <div
                    className={styles.cardBody}
                    id={`tutorial-panel-${section.id}`}
                    role="region"
                    aria-labelledby={`tutorial-trigger-${section.id}`}
                >
                    <ol className={styles.stepList}>
                        {section.steps.map((step, idx) => (
                            <StepItem key={idx} step={step} />
                        ))}
                    </ol>
                    {section.cta && (
                        <div className={styles.ctaWrap}>
                            <Link to={section.cta.to} className={styles.ctaLink}>
                                {section.cta.label}
                            </Link>
                        </div>
                    )}
                </div>
            )}
        </article>
    );
};

const TutorialsPage = () => {
    const [openId, setOpenId] = useState(TUTORIAL_SECTIONS[0].id);
    const [searchQuery, setSearchQuery] = useState('');

    const handleToggle = (id) => {
        setOpenId((prev) => (prev === id ? null : id));
    };

    const visibleSections = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return TUTORIAL_SECTIONS;
        return TUTORIAL_SECTIONS.filter((section) =>
            section.title.toLowerCase().includes(query) ||
            section.steps.some((step) => step.text.toLowerCase().includes(query))
        );
    }, [searchQuery]);

    return (
        <div className={styles.page}>
            <header className={`${styles.header} ${styles.heroHeader}`}>
                <div className={styles.headerIcon}>
                    <GraduationCap size={28} />
                </div>
                <div className={styles.heroCopy}>
                    <span className={styles.eyebrow}>LivePulse guide</span>
                    <h1 className={styles.title}>Tutorials</h1>
                    <p className={styles.subtitle}>
                        Learn the complete flow from your first event to live responses, analytics, and billing.
                    </p>
                </div>
                <Link to="/dashboard" className={styles.heroCta}>
                    Create an event <ArrowRight size={16} aria-hidden />
                </Link>
            </header>

            <section className={styles.workflow} aria-label="LivePulse workflow">
                <div className={styles.workflowStep}>
                    <span className={styles.workflowNumber}>01</span>
                    <div><strong>Create</strong><span>an event</span></div>
                </div>
                <ArrowRight className={styles.workflowArrow} size={18} aria-hidden />
                <div className={styles.workflowStep}>
                    <span className={styles.workflowNumber}>02</span>
                    <div><strong>Engage</strong><span>with activities</span></div>
                </div>
                <ArrowRight className={styles.workflowArrow} size={18} aria-hidden />
                <div className={styles.workflowStep}>
                    <span className={styles.workflowNumber}>03</span>
                    <div><strong>Present</strong><span>and measure</span></div>
                </div>
            </section>

            <section className={styles.quickNav}>
                <div className={styles.quickNavHeading}>
                    <h3 className={styles.quickNavTitle}>Jump to a workspace</h3>
                    <span className={styles.quickNavHint}>Useful places in LivePulse</span>
                </div>
                <div className={styles.quickNavLinks}>
                    <Link to="/dashboard" className={styles.quickLink}><CalendarPlus size={16} /> Dashboard</Link>
                    <Link to="/analytics" className={styles.quickLink}><BarChart3 size={16} /> Analytics</Link>
                    <Link to="/settings" className={styles.quickLink}><Coins size={16} /> Balance &amp; history</Link>
                </div>
            </section>

            <section className={styles.section}>
                <div className={styles.guidesHeader}>
                    <h2 className={styles.sectionTitle}>
                        <ListOrdered size={20} className={styles.sectionTitleIcon} />
                        Guides <span className={styles.guideCount}>{visibleSections.length}</span>
                    </h2>
                    <label className={styles.guideSearch}>
                        <Search size={16} aria-hidden />
                       
                        <input
                            type="search"
                            value={searchQuery}
                            onChange={(event) => setSearchQuery(event.target.value)}
                            placeholder="Search tutorials"
                            aria-label="Search tutorials"
                        />
                    </label>
                </div>
                <div className={styles.cardList}>
                    {visibleSections.length > 0 ? visibleSections.map((section) => (
                        <TutorialCard
                            key={section.id}
                            section={section}
                            isOpen={openId === section.id}
                            onToggle={handleToggle}
                        />
                    )) : (
                        <div className={styles.noResults}>
                            <Search size={20} aria-hidden />
                            <strong>No guides found</strong>
                            <span>Try searching for events, polls, analytics, or coins.</span>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};

export default TutorialsPage;
